import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { useApi } from '../hooks/useApi';
import { refreshBranding } from '../hooks/useBranding';

export default function AdminBrandingPage() {
  const api = useApi();
  const fileRef = useRef();
  const [logoUrl, setLogoUrl] = useState(null);
  const [poolName, setPoolName] = useState('March Madness Pool');
  const [pendingFile, setPendingFile] = useState(null);  // File object waiting to upload
  const [preview, setPreview] = useState(null);          // Local preview URL
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.getBranding()
      .then(data => {
        setLogoUrl(data.logoUrl || null);
        setPoolName(data.poolName || 'March Madness Pool');
      })
      .catch(err => console.warn('Branding load:', err.message));
  }, []);

  function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, SVG)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be under 2MB');
      return;
    }
    setError('');
    setPendingFile(file);
    // Show local preview immediately
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }

  async function handleSave() {
    setError(''); setStatus('Saving...');
    setUploading(true);
    try {
      let finalLogoUrl = logoUrl;

      // Upload to Firebase Storage if there's a pending file
      if (pendingFile) {
        setStatus('Uploading image...');
        try {
          // 10 second timeout on the upload
          const uploadPromise = (async () => {
            const storageRef = ref(storage, `branding/logo_${Date.now()}_${pendingFile.name}`);
            const snapshot = await uploadBytes(storageRef, pendingFile);
            return await getDownloadURL(snapshot.ref);
          })();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Upload timed out — check Firebase Storage is enabled in your project (Build → Storage → Get Started)')), 10000)
          );
          finalLogoUrl = await Promise.race([uploadPromise, timeoutPromise]);
          setPendingFile(null);
          setPreview(null);
          setLogoUrl(finalLogoUrl);
        } catch (uploadErr) {
          // Fall back to base64 if Storage fails, with a size check
          if (uploadErr.message.includes('timed out') || uploadErr.code === 'storage/unauthorized') {
            if (pendingFile.size > 200 * 1024) {
              throw new Error(uploadErr.message.includes('timed out')
                ? uploadErr.message
                : 'Firebase Storage not authorized. Go to Firebase Console → Build → Storage → Get Started, then set rules to allow admin writes.');
            }
            // Small file — fall back to base64
            setStatus('Storage unavailable — using base64 fallback...');
            finalLogoUrl = preview;
          } else {
            throw uploadErr;
          }
        }
      }

      setStatus('Saving branding...');
      const data = { poolName, logoUrl: finalLogoUrl };
      await api.saveBranding(data);
      refreshBranding(data);
      setStatus('✅ Branding saved! Navbar updated.');
    } catch (err) {
      setError('Save failed: ' + err.message);
      setStatus('');
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveLogo() {
    setPreview(null);
    setPendingFile(null);
    setLogoUrl(null);
    const data = { poolName, logoUrl: null };
    await api.saveBranding(data);
    refreshBranding(data);
    setStatus('Logo removed.');
  }

  const currentLogo = preview || logoUrl;

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--blue)', letterSpacing: '2px', marginBottom: 20 }}>
        BRANDING
      </h2>

      {error && <div className="alert alert-error">{error}</div>}
      {status && <div className="alert alert-success">{status}</div>}

      <div className="grid-2" style={{ gap: 24, alignItems: 'start' }}>

        {/* Left: Upload */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '1.1rem' }}>POOL LOGO</h3></div>

          <div className="alert alert-info" style={{ fontSize: '0.82rem', marginBottom: 16 }}>
            Upload a PNG, JPG, or SVG (under 2MB). Stored in Firebase Storage.
            Appears in the navbar and will be used in printable bracket PDFs.
          </div>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current.click()}
            style={{
              border: `2px dashed ${dragging ? 'var(--blue)' : 'var(--slate)'}`,
              borderRadius: 12, padding: '32px 20px', textAlign: 'center',
              cursor: 'pointer',
              background: dragging ? 'rgba(75,139,190,0.08)' : 'var(--navy-light)',
              transition: 'all 0.2s', marginBottom: 16,
            }}
          >
            {currentLogo ? (
              <div>
                <img src={currentLogo} alt="logo preview" style={{
                  maxHeight: 100, maxWidth: 200, objectFit: 'contain',
                  borderRadius: 8, marginBottom: 10,
                }} />
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--gray)' }}>
                  {pendingFile ? `📎 ${pendingFile.name} — unsaved` : 'Click or drag to replace'}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '3rem', marginBottom: 8 }}>🏀</div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontWeight: 700, color: 'var(--cream)', marginBottom: 4 }}>
                  Drop image here or click to browse
                </div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.8rem', color: 'var(--gray)' }}>
                  PNG, JPG, SVG · max 2MB · square images work best
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
          </div>

          {currentLogo && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={handleRemoveLogo} disabled={uploading}>
                Remove Logo
              </button>
              {pendingFile && (
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: '0.82rem', color: 'var(--orange)' }}>
                  ● Unsaved — will upload on Save
                </span>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Pool Name (shown in navbar)</label>
            <input className="form-input" value={poolName}
              onChange={e => setPoolName(e.target.value)}
              placeholder="e.g. March Madness 2025" maxLength={40} />
          </div>

          <button className="btn btn-primary" onClick={handleSave} disabled={uploading}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {uploading
              ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span> {status}</>
              : '💾 Save Branding'}
          </button>
        </div>

        {/* Right: Preview */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ fontSize: '1.1rem' }}>NAVBAR PREVIEW</h3></div>
          <div style={{
            background: 'var(--navy-mid)', borderRadius: 8, padding: '12px 20px',
            display: 'flex', alignItems: 'center', gap: 10,
            border: '1px solid var(--slate)', marginBottom: 20,
          }}>
            {currentLogo
              ? <img src={currentLogo} alt="logo" style={{ height: 32, width: 32, objectFit: 'contain', borderRadius: 4 }} />
              : <span style={{ fontSize: '1.4rem' }}>🏀</span>}
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--blue)', letterSpacing: '2px' }}>
              {poolName ? poolName.toUpperCase() : 'MARCH MADNESS'}
            </span>
          </div>

          <div className="card-header" style={{ marginBottom: 12 }}>
            <h3 className="card-title" style={{ fontSize: '1rem' }}>PDF CENTER PREVIEW</h3>
          </div>
          <div style={{
            background: 'white', borderRadius: 8, padding: 24,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: 160, border: '1px solid var(--slate)',
          }}>
            {currentLogo ? (
              <>
                <img src={currentLogo} alt="logo" style={{ maxHeight: 80, maxWidth: 160, objectFit: 'contain', marginBottom: 8 }} />
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 700, color: '#1a2e45', textAlign: 'center' }}>
                  {poolName}
                </div>
              </>
            ) : (
              <div style={{ color: '#999', fontFamily: 'Arial, sans-serif', fontSize: '13px', textAlign: 'center' }}>
                🏀<br />Upload a logo to see PDF preview
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
